import { useEffect, useRef, useState } from 'react';
import { catalogLayers, svgUrl, type CatalogItem } from '../../services/catalog';
import { catalogPreviewSvg } from './renderCatalogPreview';
import { tileUrl } from './tileUrl';

/** Server thumbnails remain preferred; existing saved layers can draw their own preview. */
export function CatalogPreview({ item, fit = 'contain' }: { item: CatalogItem; fit?: 'contain' | 'cover' }) {
  const host = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const [local, setLocal] = useState<{ id: string; src: string | null } | null>(null);
  const remote = item.thumbnailUrl ? tileUrl(item.thumbnailUrl) : item.svg ? svgUrl(item.svg) : null;
  const source = remote && remote !== failedSource ? remote : local?.id === item.id ? local.src : null;

  useEffect(() => {
    if (!host.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: '160px' });
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || source || item.format !== 'layers' || local?.id === item.id) return;
    let live = true;
    void catalogLayers(item.id).then(layers => {
      if (!layers) throw new Error('No saved layers.');
      return svgUrl(catalogPreviewSvg(item, layers));
    }).then(src => {
      if (live) setLocal({ id: item.id, src });
    }).catch(() => {
      if (live) setLocal({ id: item.id, src: null });
    });
    return () => { live = false; };
  }, [visible, source, item.id, item.format, item.kind, item.width, item.height, local?.id]);

  return <span ref={host} className={`absolute grid min-h-0 min-w-0 place-items-center ${fit === 'contain' ? 'inset-[9px]' : 'inset-0'}`}>
    {source ? <img src={source} alt="" loading="lazy" draggable={false} className={`absolute inset-0 block size-full ${fit === 'cover' ? 'object-cover' : 'object-contain'}`} onError={() => {
      if (source === remote) setFailedSource(source);
      else setLocal({ id: item.id, src: null });
    }} /> : <span className="px-1 text-center text-[10px] leading-tight text-label-2">{local?.id === item.id || (failedSource && item.format !== 'layers') ? 'Preview unavailable' : 'Loading preview…'}</span>}
  </span>;
}
